import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import { TID } from "@/lib/testIds";

export const AdminLogin = ({ onSuccess }) => {
    const [loading, setLoading] = useState(false);
    // Two-step MFA — after the password is verified, if the admin has TOTP
    // enabled the backend responds 401 with detail=MFA_REQUIRED. We keep
    // email+password in state and re-submit with the code.
    const [needsMfa, setNeedsMfa] = useState(false);
    const [creds, setCreds] = useState({ email: "", password: "" });
    const [mfaCode, setMfaCode] = useState("");

    const submit = async (email, password, mfa_code) => {
        try {
            setLoading(true);
            const { data } = await api.post("/admin/login", { email, password, mfa_code });
            localStorage.setItem("simonara_admin_token", data.token);
            toast.success("Welcome back, admin.");
            onSuccess();
        } catch (err) {
            const detail = err?.response?.data?.detail || "";
            if (detail === "MFA_REQUIRED") {
                setNeedsMfa(true);
                setCreds({ email, password });
                return;
            }
            toast.error(detail || formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const onPasswordSubmit = (e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        submit(fd.get("email"), fd.get("password"), "");
    };

    const onMfaSubmit = (e) => {
        e.preventDefault();
        submit(creds.email, creds.password, mfaCode.trim());
    };

    return (
        <section className="min-h-[80vh] flex items-center bg-brand-navy">
            <div className="container-page max-w-md">
                <div className="card-soft p-8">
                    <div className="w-12 h-12 rounded-xl bg-brand-navy text-white flex items-center justify-center">
                        {needsMfa ? <ShieldCheck size={20} /> : <Lock size={20} />}
                    </div>
                    <h1 className="mt-5 font-heading font-bold text-2xl text-brand-navy dark:text-slate-100">
                        {needsMfa ? "Two-factor code" : "Admin Sign In"}
                    </h1>
                    <p className="text-brand-muted text-sm mt-1">
                        {needsMfa
                            ? "Open your authenticator app and enter the 6-digit code."
                            : "Manage jobs and review form submissions."}
                    </p>

                    {!needsMfa ? (
                        <form onSubmit={onPasswordSubmit} className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="admin-email">Email</Label>
                                <Input id="admin-email" name="email" type="email" data-testid={TID.admin.email} required className="mt-2" />
                            </div>
                            <div>
                                <Label htmlFor="admin-pw">Password</Label>
                                <Input id="admin-pw" name="password" type="password" data-testid={TID.admin.password} required className="mt-2" />
                            </div>
                            <button type="submit" disabled={loading} data-testid={TID.admin.loginBtn} className="btn-navy w-full disabled:opacity-60">
                                {loading ? <><Loader2 size={18} className="mr-2 animate-spin" /> Signing in...</> : "Sign in"}
                            </button>
                            <Link
                                to="/admin/forgot-password"
                                data-testid="admin-forgot-link"
                                className="block text-center text-xs text-muted-foreground hover:text-foreground underline"
                            >
                                Forgot your password?
                            </Link>
                        </form>
                    ) : (
                        <form onSubmit={onMfaSubmit} className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="admin-mfa">6-digit code</Label>
                                <Input
                                    id="admin-mfa"
                                    autoFocus
                                    inputMode="numeric"
                                    pattern="\d{6}"
                                    maxLength={6}
                                    value={mfaCode}
                                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                                    data-testid="admin-mfa-code"
                                    className="mt-2 tracking-widest text-center text-lg"
                                    required
                                />
                            </div>
                            <button type="submit" disabled={loading || mfaCode.length !== 6} data-testid="admin-mfa-submit" className="btn-navy w-full disabled:opacity-60">
                                {loading ? <><Loader2 size={18} className="mr-2 animate-spin" /> Verifying...</> : "Verify"}
                            </button>
                            <button type="button" onClick={() => { setNeedsMfa(false); setMfaCode(""); }} className="text-xs text-muted-foreground hover:text-foreground underline w-full text-center">
                                Use a different account
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};

export default AdminLogin;
